import { Badge, Button, Flex, IconButton, Input, Text } from "@chakra-ui/react"
import type { ChangeEvent, KeyboardEvent } from "react"
import { useState } from "react"
import { FiX } from "react-icons/fi"

interface TagInputProps {
  tags: string[]
  suggestions?: string[]
  onChange: (tags: string[]) => void
}

export function TagInput({ tags, suggestions = [], onChange }: TagInputProps) {
  const [inputValue, setInputValue] = useState("")
  const availableSuggestions = suggestions
    .filter((tag) => !tags.includes(tag))
    .slice(0, 8)

  function addTag(rawTag: string) {
    const normalized = rawTag.trim().toLowerCase()
    if (!normalized || tags.includes(normalized)) return

    onChange([...tags, normalized])
    setInputValue("")
  }

  function removeTag(tag: string) {
    onChange(tags.filter((existingTag) => existingTag !== tag))
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value
    if (value.endsWith(",")) {
      addTag(value.slice(0, -1))
      return
    }

    setInputValue(value)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== "Enter") return

    event.preventDefault()
    addTag(inputValue)
  }

  return (
    <Flex direction="column" gap={2}>
      <Flex wrap="wrap" gap={2} minH="32px">
        {tags.map((tag) => (
          <Badge key={tag} colorPalette="blue" size="md" px={2} py={1}>
            <Flex align="center" gap={1}>
              <Text>{tag}</Text>
              <IconButton
                aria-label={`Remove ${tag}`}
                size="2xs"
                variant="ghost"
                onClick={() => removeTag(tag)}
              >
                <FiX />
              </IconButton>
            </Flex>
          </Badge>
        ))}
      </Flex>
      <Input
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder="Add a tag..."
        size="sm"
      />
      {availableSuggestions.length > 0 && (
        <Flex wrap="wrap" gap={2}>
          {availableSuggestions.map((tag) => (
            <Button
              key={tag}
              size="xs"
              variant="outline"
              colorPalette="blue"
              onClick={() => addTag(tag)}
            >
              {tag}
            </Button>
          ))}
        </Flex>
      )}
      <Text fontSize="xs" color="gray.500">
        Press Enter or type a comma to add a tag.
      </Text>
    </Flex>
  )
}
